import { useState } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { Modal, ModalBody, Button, Close } from 'chaoskit/src/components'
import { link, text } from 'chaoskit/src/assets/styles/utility'
import { StylesIconVariables } from 'chaoskit/src/components/Icon'

import Navigation, { navTransition } from './Navigation'

import ButtonLink from '~components/ButtonLink'
import TextLink from '~components/TextLink'
import Icon from '~components/Icon'
import logoMascot from '~media/global/mascot.svg'
import { ReactComponent as ProductSVG } from '~media/navigation/platform.svg'
import { ReactComponent as PricingSVG } from '~media/navigation/pricing.svg'
import { ReactComponent as SlackSVG } from '~media/navigation/slack.svg'
import { ReactComponent as ChaosConfSVG } from '~media/navigation/chaosconf.svg'
import { ReactComponent as FailoverConfSVG } from '~media/navigation/failoverconf.svg'
import { ReactComponent as EventsSVG } from '~media/navigation/events.svg'
import { ReactComponent as CustomersSVG } from '~media/navigation/customers.svg'
import { ReactComponent as TeamSVG } from '~media/navigation/team.svg'
import { ReactComponent as PressSVG } from '~media/navigation/press.svg'
import { ReactComponent as LeadershipSVG } from '~media/navigation/leadership.svg'
import { ReactComponent as BlogSVG } from '~media/navigation/blog.svg'
import { ReactComponent as DocsSVG } from '~media/navigation/docs.svg'
import { ReactComponent as TutorialsSVG } from '~media/navigation/tutorials.svg'
import { ReactComponent as ResourcesSVG } from '~media/navigation/resources.svg'
import { ReactComponent as SecuritySVG } from '~media/navigation/security.svg'
import { ReactComponent as BootCampsSVG } from '~media/navigation/bootcamps.svg'
import Image from '~components/Image'

const MobileSectionHeader = ({ children, ...rest }) => {
  const theme = useTheme()

  return (
    /* eslint-disable-next-line jsx-a11y/heading-has-content */
    <div
      css={{
        ...text.expanded(theme),
        fontWeight: theme.fontWeight.bold,
        ...theme.text.xsmall,
        marginTop: theme.space.large,
        textAlign: 'center',
        marginBottom: theme.space.large,
        color: theme.contrast.base,
      }}
      {...rest}
    >
      <span
        css={{
          position: 'relative',
          zIndex: 1,

          '&::before': {
            content: "''",
            background: theme.fontColor.base,
            top: -theme.space.xsmall,
            bottom: -theme.space.xsmall,
            left: -theme.space.small,
            right: -theme.space.small,
            transform: 'skew(-15deg)',
            position: 'absolute',
            zIndex: -1,
          },
        }}
      >
        {children}
      </span>
    </div>
  )
}

MobileSectionHeader.propTypes = {
  children: PropTypes.node.isRequired,
}

const MobileLink = (props) => {
  const theme = useTheme()

  return (
    <TextLink
      css={[link.reset(theme), { fontWeight: theme.fontWeight.bold }]}
      {...props}
    />
  )
}

const MobileNavGrid = (props) => {
  const theme = useTheme()

  return (
    <nav
      css={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: theme.space.base,
      }}
      {...props}
    />
  )
}

const MobileNavGridItem = ({
  href,
  hrefSeparator = '?',
  title,
  svg: SVGComponent,
}) => {
  const theme = useTheme()

  return (
    <div css={{ position: 'relative' }}>
      <TextLink
        href={`${href}${hrefSeparator}ref=nav`}
        css={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 2,

          '&:hover, &:focus': {
            '+ .Global__Header__MobileNavWrapper svg': {
              filter: 'grayscale(0)',
              opacity: 1,
            },
          },
        }}
      />
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: `${theme.space.base}px min-content`,
          gap: theme.space.small,
          alignItems: 'center',
        }}
        className="Global__Header__MobileNavWrapper"
      >
        <SVGComponent
          css={{
            filter: 'grayscale(1)',
            opacity: theme.opacity.base,
            transition: `filter ${navTransition(
              theme
            )}, opacity ${navTransition(theme)}`,
          }}
        />
        <div>
          <div
            css={{
              whiteSpace: 'nowrap',
              fontWeight: theme.fontWeight.bold,
            }}
          >
            {title}
          </div>
        </div>
      </div>
    </div>
  )
}

MobileNavGridItem.propTypes = {
  href: PropTypes.string.isRequired,
  hrefSeparator: PropTypes.string,
  svg: PropTypes.any.isRequired,
  title: PropTypes.string.isRequired,
}

const MobileNav = ({ contrast, isStuck }) => {
  const theme = useTheme()

  const [isOpen, setIsOpen] = useState(false)

  return (
    <Navigation css={{ [theme.mq.medium]: { display: 'none' } }}>
      <Modal open={isOpen} setIsOpen={setIsOpen}>
        <ModalBody css={{ paddingBottom: 0 }}>
          <Close
            onClick={() => setIsOpen(false)}
            css={{
              position: 'absolute',
              top: theme.space.base,
              right: theme.space.base,
              zIndex: 10,
            }}
          />
          <TextLink
            href="/"
            css={{
              display: 'block',
              marginLeft: 'auto',
              marginRight: 'auto',
              marginTop: theme.space.base,
              marginBottom: theme.space.large,
              width: theme.space.xlarge,
            }}
          >
            <Image src={logoMascot} />
          </TextLink>
          <MobileNavGrid>
            <MobileNavGridItem
              title="Product"
              href="/product/"
              svg={ProductSVG}
            />
            <MobileNavGridItem
              title="Pricing"
              href="/pricing/"
              svg={PricingSVG}
            />
          </MobileNavGrid>
          <MobileSectionHeader>Resources</MobileSectionHeader>
          <MobileNavGrid>
            <MobileNavGridItem title="Blog" href="/blog/" svg={BlogSVG} />
            <MobileNavGridItem title="Docs" href="/docs/" svg={DocsSVG} />
            <MobileNavGridItem
              title="Tutorials"
              href="/community/tutorials/"
              svg={TutorialsSVG}
            />
            <MobileNavGridItem
              title="Security"
              href="/security/"
              svg={SecuritySVG}
            />
            <MobileNavGridItem
              title="Bootcamps"
              href="/bootcamps/"
              svg={BootCampsSVG}
            />
            <MobileNavGridItem
              title="Search all"
              href="/resources/"
              svg={ResourcesSVG}
            />
          </MobileNavGrid>
          <MobileSectionHeader>Community</MobileSectionHeader>
          <MobileNavGrid>
            <MobileNavGridItem
              title="Chaos Community"
              href="/community/"
              svg={SlackSVG}
            />
            <MobileNavGridItem
              title="Chaos Conf"
              href="/chaos-conf/2020/"
              svg={ChaosConfSVG}
            />
            <MobileNavGridItem
              title="Events"
              href="/resources/?refinementList%5BarticleLabel%5D%5B0%5D=Events&refinementList%5BarticleLabel%5D%5B1%5D=Webinars&page=1&configure%5BfacetingAfterDistinct%5D=true"
              hrefSeparator="&"
              svg={EventsSVG}
            />
            <MobileNavGridItem
              title="Failover Conf"
              href="/failoverconf/2021/"
              svg={FailoverConfSVG}
            />
          </MobileNavGrid>
          <MobileSectionHeader>Company</MobileSectionHeader>
          <MobileNavGrid>
            <MobileNavGridItem
              title="Customers"
              href="/customers/"
              svg={CustomersSVG}
            />
            <MobileNavGridItem
              title="Team &amp; Careers"
              href="/team/"
              svg={TeamSVG}
            />
            <MobileNavGridItem
              title="Press"
              href="/press/about/"
              svg={PressSVG}
            />
            <MobileNavGridItem
              title="Leadership"
              href="/leadership/"
              svg={LeadershipSVG}
            />
          </MobileNavGrid>
          <footer
            css={{
              marginLeft: -theme.space.large,
              marginRight: -theme.space.large,
              marginTop: theme.space.large,
              borderBottomLeftRadius: theme.borderRadius.large,
              borderBottomRightRadius: theme.borderRadius.large,
              background: theme.color.panel.base,
              display: 'grid',
              gridTemplateColumns: 'repeat(2, min-content)',
              justifyContent: 'center',
              gap: theme.space.large,
              alignItems: 'center',
              padding: theme.space.base,
              borderTop: theme.border.base,
            }}
          >
            <MobileLink
              href="https://app.gremlin.com/?ref=nav"
              css={{ whiteSpace: 'nowrap' }}
            >
              Login
              <Icon
                icon="arrow-right"
                css={{ marginLeft: theme.space.xsmall }}
                size="small"
              />
            </MobileLink>
            <ButtonLink type="primary" url="/get-started/?ref=nav" size="small">
              Get started
            </ButtonLink>
          </footer>
        </ModalBody>
      </Modal>
      <Button
        size="small"
        iconOnly
        onClick={() => setIsOpen(true)}
        title="Open menu"
        css={{
          color: (contrast || isStuck) && theme.color.light.base,
          svg: {
            width: StylesIconVariables.medium,
            height: StylesIconVariables.medium,
          },
        }}
      >
        <Icon icon="menu" />
      </Button>
    </Navigation>
  )
}

MobileNav.propTypes = {
  contrast: PropTypes.bool,
  isStuck: PropTypes.bool,
}

export default MobileNav
